/**
 * PSD import — group-based Procreate layer schema (the "Layer Schema" brief).
 *
 * Each tile instance is a Procreate Layer Group whose identity is carried by the
 * QR baked into its `Window` layer (NOT the group name — Procreate copies names
 * verbatim on duplicate). We walk the tree, resolve each group's instance
 * descriptor `(shape, style)` from its Window QR, composite the group's ARTWORK
 * (everything except the reserved `Window`/`Outline` layers) with full fidelity
 * — per-layer opacity, blend mode, and any per-layer ag-psd mask — then confine
 * the result to the Window's transparent HOLE (the true tile shape, v2 stencil)
 * and gather groups that resolve to the same instance (e.g. a user-duplicated
 * group) into one bitmap. The result feeds the
 * existing wallpaper tiling pipeline exactly like the old flat-layer extraction:
 * `extractPaintRegions` still returns `ExtractedRegion[]`, so psdRender / the
 * layers store / tileRenderer are unchanged.
 *
 * ag-psd exposes no per-group flattened pixels (groups carry no canvas), so the
 * compositing here is hand-rolled over each group's child canvases, using
 * Canvas2D `globalCompositeOperation` for blend modes.
 */
import { type Layer, type Psd } from 'ag-psd';
import jsQR from 'jsqr';
import { tileRectFromUnitRect } from '@/services/psd/psdData';
import { PAINT_LAYER, WINDOW_LAYER, isReservedLayerName, parseInstance, type InstanceDescriptor } from '@/services/psd/psdSchema';
import { TILE_RASTER } from '@/stores/layers';

/** Upper bound on an imported region raster's pixel dimensions — the region
 *  extent can be considerably larger than the unit cell (paint legitimately
 *  spills outside [0,1]²), so this is generous headroom above TILE_RASTER. */
const MAX_REGION_RASTER = 2048;

/** Flip to true to trace import decisions to the console. Compile-time const so
 *  the guarded calls tree-shake out of a production build. */
const DEBUG = false;
export function psdDebug(...args: unknown[]): void {
  if (DEBUG) console.debug('[psd]', ...args);
}

export interface ExtractedRegion {
  regionName: string;
  /** 1-based colour-tiling instance (style) number, absent for a single-style project. */
  colourInstance?: number;
  canvas: HTMLCanvasElement;
  /** Tile-space rect [x0,y0,x1,y1] that `canvas` maps onto — the FULL PSD
   *  canvas's extent in tile-space (see `tileRectFromUnitRect`). */
  rect: [number, number, number, number];
}

export interface PaintExtraction {
  /** One merged bitmap per painted instance. */
  regions: ExtractedRegion[];
  /** Human-facing names of instances that received painted artwork. */
  imported: string[];
  /** Region (shape) names present as a resolved group in the file, painted or not. */
  regionsPresent: string[];
  /** Declared region names with no corresponding group at all. */
  missingRegions: string[];
  /** Instances that don't map to a known region, or groups whose Data QR
   *  wouldn't decode (flagged + dropped — brief step 6, fail loud). */
  unmatched: string[];
}

/** Global-metadata QR layer prefix (top-level, distinct from the per-group `Data`). */
const DATA_PREFIX = 'Escherllate Data - ';

function flattenLayers(children: Layer[] | undefined): Layer[] {
  if (!children) return [];
  const out: Layer[] = [];
  for (const l of children) {
    if (l.children) out.push(...flattenLayers(l.children));
    else out.push(l);
  }
  return out;
}

/** True if any pixel's alpha exceeds `threshold`. */
export function imageDataHasContent(data: Uint8ClampedArray, threshold = 0): boolean {
  for (let i = 3; i < data.length; i += 4) {
    if (data[i]! > threshold) return true;
  }
  return false;
}

function docCanvas(w: number, h: number): { canvas: HTMLCanvasElement; ctx: CanvasRenderingContext2D } {
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  return { canvas, ctx: canvas.getContext('2d')! };
}

/** Multiply the alpha channel of `ctx` (a docW×docH context) by the greyscale
 *  coverage of `coverage` (a mask/silhouette canvas at offset mx,my). Coverage
 *  = luminance × alpha; pixels outside the coverage bounds are cleared (standard
 *  PSD masking). Shared by per-layer mask application and clip-to-silhouette. */
function multiplyAlphaByCoverage(
  ctx: CanvasRenderingContext2D,
  coverage: HTMLCanvasElement,
  mx: number,
  my: number,
  docW: number,
  docH: number,
): void {
  const cw = coverage.width;
  const ch = coverage.height;
  const cData = coverage.getContext('2d')!.getImageData(0, 0, cw, ch).data;
  const img = ctx.getImageData(0, 0, docW, docH);
  const px = img.data;
  for (let y = 0; y < docH; y++) {
    for (let x = 0; x < docW; x++) {
      const o = (y * docW + x) * 4;
      const lx = x - mx;
      const ly = y - my;
      let cover = 0;
      if (lx >= 0 && lx < cw && ly >= 0 && ly < ch) {
        const co = (ly * cw + lx) * 4;
        cover = (cData[co]! * (cData[co + 3]! / 255)) / 255;
      }
      px[o + 3] = Math.round(px[o + 3]! * cover);
    }
  }
  ctx.putImageData(img, 0, 0);
}

/** Apply a layer's own ag-psd mask (if present + enabled) to a docW×docH ctx. */
function applyLayerMask(ctx: CanvasRenderingContext2D, layer: Layer, docW: number, docH: number): void {
  const mask = layer.mask;
  if (!mask || mask.disabled || !mask.canvas) return;
  multiplyAlphaByCoverage(ctx, mask.canvas, mask.left ?? 0, mask.top ?? 0, docW, docH);
}

/** Confine `ctx` to the Window's transparent HOLE (v2 stencil, brief step 4):
 *  keep where the Window is transparent (inside the tile shape), drop where it
 *  is opaque (the cover). PURE ALPHA — the cover's colour is arbitrary, only its
 *  alpha is load-bearing — so keep-factor = 1 − windowAlpha/255. Pixels outside
 *  the Window's bounds are kept (no cover there); the Window is full-canvas in
 *  practice, so that's defensive only. */
function confineToHole(
  ctx: CanvasRenderingContext2D,
  win: HTMLCanvasElement,
  wx: number,
  wy: number,
  docW: number,
  docH: number,
): void {
  const ww = win.width;
  const wh = win.height;
  const wData = win.getContext('2d')!.getImageData(0, 0, ww, wh).data;
  const img = ctx.getImageData(0, 0, docW, docH);
  const px = img.data;
  for (let y = 0; y < docH; y++) {
    for (let x = 0; x < docW; x++) {
      const o = (y * docW + x) * 4;
      const lx = x - wx;
      const ly = y - wy;
      let keep = 1;
      if (lx >= 0 && lx < ww && ly >= 0 && ly < wh) {
        keep = 1 - wData[(ly * ww + lx) * 4 + 3]! / 255;
      }
      px[o + 3] = Math.round(px[o + 3]! * keep);
    }
  }
  ctx.putImageData(img, 0, 0);
}

/** Procreate/PSD blend mode → Canvas2D globalCompositeOperation. Modes without
 *  an exact Canvas equivalent fall back to source-over (Normal) — logged. */
const BLEND_OP: Partial<Record<string, GlobalCompositeOperation>> = {
  normal: 'source-over',
  darken: 'darken',
  multiply: 'multiply',
  'color burn': 'color-burn',
  lighten: 'lighten',
  screen: 'screen',
  'color dodge': 'color-dodge',
  'linear dodge': 'lighter',
  overlay: 'overlay',
  'soft light': 'soft-light',
  'hard light': 'hard-light',
  difference: 'difference',
  exclusion: 'exclusion',
  hue: 'hue',
  saturation: 'saturation',
  color: 'color',
  luminosity: 'luminosity',
};
function blendOp(mode: string | undefined): GlobalCompositeOperation {
  if (!mode) return 'source-over';
  const op = BLEND_OP[mode];
  if (!op) {
    psdDebug('unmapped blend mode → normal', mode);
    return 'source-over';
  }
  return op;
}

/**
 * Composite one instance group's ARTWORK to a docW×docH canvas, or null if the
 * group is empty. Reserved layers (`Window`/`Outline`) and hidden layers are
 * excluded from colour; remaining layers composite bottom→top (children[0] =
 * bottom) honouring opacity, blend mode, and each layer's own ag-psd mask.
 * There is no clipping to handle (v2 has none). Finally the whole result is
 * confined to the Window's transparent hole — the true tile shape — since a
 * user could have painted past the hole edge in Procreate's stencil view.
 */
function compositeGroupArtwork(children: Layer[], docW: number, docH: number): HTMLCanvasElement | null {
  const win = children.find((c) => c.name === WINDOW_LAYER && c.canvas);
  const artwork = children.filter((c) => !!c.canvas && !isReservedLayerName(c.name) && !c.hidden);
  if (artwork.length === 0) return null;

  const { canvas: acc, ctx: actx } = docCanvas(docW, docH);
  for (const layer of artwork) {
    const { canvas: temp, ctx: tctx } = docCanvas(docW, docH);
    tctx.drawImage(layer.canvas!, layer.left ?? 0, layer.top ?? 0);
    applyLayerMask(tctx, layer, docW, docH);
    actx.globalAlpha = layer.opacity ?? 1;
    actx.globalCompositeOperation = blendOp(layer.blendMode);
    actx.drawImage(temp, 0, 0);
    actx.globalAlpha = 1;
    actx.globalCompositeOperation = 'source-over';
  }

  if (win?.canvas) confineToHole(actx, win.canvas, win.left ?? 0, win.top ?? 0, docW, docH);
  if (!imageDataHasContent(actx.getImageData(0, 0, docW, docH).data)) return null;
  return acc;
}

/** Stack several doc-sized canvases (source-over, in the given order) into one —
 *  for gathering duplicate groups of the same instance. */
function stackCanvases(canvases: readonly HTMLCanvasElement[], docW: number, docH: number): HTMLCanvasElement {
  const { canvas, ctx } = docCanvas(docW, docH);
  for (const c of canvases) ctx.drawImage(c, 0, 0);
  return canvas;
}

/** Scale a full-doc canvas to the render pipeline's raster resolution and report
 *  the tile-space rect it maps onto (from `unitRect`). No `unitRect` (shouldn't
 *  happen — psdRender hard-rejects) ⇒ whole canvas → unit square. */
function scaleToRegionRaster(
  full: HTMLCanvasElement,
  docW: number,
  docH: number,
  unitRect?: readonly [number, number, number, number],
): { canvas: HTMLCanvasElement; rect: [number, number, number, number] } {
  let outW = TILE_RASTER;
  let outH = TILE_RASTER;
  let rect: [number, number, number, number] = [0, 0, 1, 1];
  if (unitRect) {
    const [x0, y0, x1, y1] = unitRect;
    const sw = x1 - x0;
    const sh = y1 - y0;
    if (sw > 0 && sh > 0) {
      outW = Math.min(MAX_REGION_RASTER, Math.max(1, Math.round((TILE_RASTER * docW) / sw)));
      outH = Math.min(MAX_REGION_RASTER, Math.max(1, Math.round((TILE_RASTER * docH) / sh)));
      rect = tileRectFromUnitRect(unitRect, docW, docH);
    }
  }
  const { canvas, ctx } = docCanvas(outW, outH);
  ctx.drawImage(full, 0, 0, docW, docH, 0, 0, outW, outH);
  return { canvas, rect };
}

/** A dihedral orientation Procreate may bake into an exported PSD (these four
 *  preserve the document's dimensions; a 90°/270° turn would swap them). The
 *  Window QR is a fiducial: baked upright, so its detected corners reveal the
 *  transform the whole canvas underwent, which we then invert. */
export type Orient = 'identity' | 'flipX' | 'flipY' | 'rot180' | 'unknown';

/** The dihedral orientation implied by a QR's located corners (pure, testable).
 *  The QR is baked upright, so its +x axis (TL→TR) and +y axis (TL→BL) reveal
 *  how the whole canvas was transformed. Only dimension-preserving cases are
 *  classified; a 90°/270° turn (QR +x gone vertical) returns 'unknown'. */
export function dihedralOrientation(tl: { x: number; y: number }, tr: { x: number; y: number }, bl: { x: number; y: number }): Orient {
  const exx = tr.x - tl.x; // QR local +x, image space
  const exy = tr.y - tl.y;
  const eyy = bl.y - tl.y; // QR local +y, y-component
  if (Math.abs(exx) < Math.abs(exy)) return 'unknown'; // 90°/270° — dims would differ
  const sx = exx >= 0 ? 1 : -1;
  const sy = eyy >= 0 ? 1 : -1;
  return sx > 0 ? (sy > 0 ? 'identity' : 'flipY') : sy > 0 ? 'flipX' : 'rot180';
}

/** Decode a group's Window QR: the instance descriptor AND the canvas
 *  orientation (from the QR's located corners). jsQR decodes the payload
 *  regardless of rotation, and its corner positions tell us how the content is
 *  oriented relative to the upright export. */
function readWindowQr(windowCanvas: HTMLCanvasElement): { desc: InstanceDescriptor | null; orient: Orient } {
  const ctx = windowCanvas.getContext('2d');
  if (!ctx) return { desc: null, orient: 'identity' };
  const { width, height } = windowCanvas;
  const r = jsQR(ctx.getImageData(0, 0, width, height).data, width, height);
  if (!r) return { desc: null, orient: 'identity' };
  const desc = parseInstance(r.data.replace(/^\d+\/\d+\|/, '')); // strip the chunk tag
  const { topLeftCorner: tl, topRightCorner: tr, bottomLeftCorner: bl } = r.location;
  return { desc, orient: dihedralOrientation(tl, tr, bl) };
}

/** Undo a dihedral orientation on a full-canvas bitmap (each op is its own
 *  inverse). Used to bring Procreate-transformed content back upright before it
 *  is registered to tile space via `unitRect` (which describes the upright
 *  export). */
function applyOrientation(src: HTMLCanvasElement, orient: Orient): HTMLCanvasElement {
  if (orient === 'identity' || orient === 'unknown') return src;
  const { canvas, ctx } = docCanvas(src.width, src.height);
  if (orient === 'rot180') {
    ctx.translate(src.width, src.height);
    ctx.rotate(Math.PI);
  } else if (orient === 'flipX') {
    ctx.translate(src.width, 0);
    ctx.scale(-1, 1);
  } else {
    ctx.translate(0, src.height);
    ctx.scale(1, -1);
  }
  ctx.drawImage(src, 0, 0);
  return canvas;
}

interface FoundGroup {
  desc: InstanceDescriptor;
  group: Layer;
  orient: Orient;
}

/** Depth-first walk collecting instance groups (a group with a decodable
 *  `Window` QR). A user-made non-instance group is recursed into; an instance
 *  group is not (its children are its own artwork). A group that HAS a `Window`
 *  child whose QR fails to decode is flagged as dropped (fail loud). */
function findInstanceGroups(children: Layer[] | undefined, found: FoundGroup[], dropped: Layer[]): void {
  for (const l of children ?? []) {
    if (!l.children) continue;
    const windowChild = l.children.find((c) => c.name === WINDOW_LAYER && c.canvas);
    if (windowChild) {
      const { desc, orient } = readWindowQr(windowChild.canvas!);
      if (desc) found.push({ desc, group: l, orient });
      else dropped.push(l);
      continue; // don't recurse into an instance group
    }
    findInstanceGroups(l.children, found, dropped); // plain user group — look inside
  }
}

/** True if the PSD looks like an Escherllate group export (has any reserved
 *  schema layer or a Paint layer) — distinguishes a data-layer-missing export
 *  from an unrelated file. */
export function hasPaintLayers(psd: Psd): boolean {
  return flattenLayers(psd.children).some((l) => l.name === PAINT_LAYER || isReservedLayerName(l.name));
}

/** Raw RGBA of each top-level global `Escherllate Data - N` QR layer. */
export function dataLayerImages(psd: Psd): { data: Uint8ClampedArray; width: number; height: number }[] {
  const out: { data: Uint8ClampedArray; width: number; height: number }[] = [];
  for (const l of flattenLayers(psd.children)) {
    if (typeof l.name !== 'string' || !l.name.startsWith(DATA_PREFIX) || !l.canvas) continue;
    const ctx = l.canvas.getContext('2d');
    if (!ctx) continue;
    const { width, height } = l.canvas;
    out.push({ data: ctx.getImageData(0, 0, width, height).data, width, height });
  }
  return out;
}

/**
 * Extract every painted instance as one merged bitmap. `regionNames` maps a
 * 1-based shape index → region name (the global metadata's `regions`);
 * `colourCount` is the project's style count (so a single-style project reports
 * no `colourInstance`); `unitRect` sets the tile-space mapping. No store
 * mutation — the caller decides what to do with the result.
 */
export function extractPaintRegions(
  psd: Psd,
  regionNames: readonly string[],
  colourCount: number,
  unitRect?: readonly [number, number, number, number],
): PaintExtraction {
  const found: FoundGroup[] = [];
  const dropped: Layer[] = [];
  findInstanceGroups(psd.children, found, dropped);
  psdDebug('instance groups', { found: found.map((f) => f.desc), dropped: dropped.length });

  const regionsPresent = new Set<string>();
  const unmatched = new Set<string>();
  if (dropped.length > 0) unmatched.add(`${dropped.length} unidentified group(s)`);

  // Composite each group's artwork; bucket by (shape, style) so duplicated
  // groups of the same instance merge (brief step 4 — "just more paint").
  const byInstance = new Map<string, { desc: InstanceDescriptor; canvases: HTMLCanvasElement[] }>();
  for (const { desc, group, orient } of found) {
    const shapeName = regionNames[desc.shape - 1];
    if (shapeName) regionsPresent.add(shapeName);
    if (orient === 'unknown') psdDebug('group has an unsupported (90°/270°) orientation — left uncorrected', desc);
    const key = `${desc.shape}:${desc.style}`;
    if (!byInstance.has(key)) byInstance.set(key, { desc, canvases: [] });
    // Composite + confine in the file's own (possibly Procreate-rotated) space,
    // then bring the result upright so it registers to tile space via unitRect
    // (which describes the upright export). The Window QR fiducial gave `orient`.
    const art = compositeGroupArtwork(group.children!, psd.width, psd.height);
    if (art) byInstance.get(key)!.canvases.push(applyOrientation(art, orient));
  }

  const regions: ExtractedRegion[] = [];
  const imported: string[] = [];
  for (const { desc, canvases } of byInstance.values()) {
    if (canvases.length === 0) continue; // instance present but unpainted
    const merged = canvases.length === 1 ? canvases[0]! : stackCanvases(canvases, psd.width, psd.height);
    const { canvas, rect } = scaleToRegionRaster(merged, psd.width, psd.height, unitRect);
    const shapeName = regionNames[desc.shape - 1] ?? `Shape ${desc.shape}`;
    const colourInstance = colourCount > 1 ? desc.style : undefined;
    regions.push({ regionName: shapeName, colourInstance, canvas, rect });
    imported.push(colourInstance !== undefined ? `${shapeName} — Style ${colourInstance}` : shapeName);
    if (!regionNames[desc.shape - 1]) unmatched.add(shapeName);
  }

  return {
    regions,
    imported,
    regionsPresent: [...regionsPresent],
    missingRegions: regionNames.filter((n) => !regionsPresent.has(n)),
    unmatched: [...unmatched],
  };
}
